import 'module-alias/register';
import deepCleaner from 'deep-cleaner';
import fs from 'fs';
import minimist from 'minimist';
import path from 'path';
import prompts from 'prompts';
import {
  ContensisMigrationService,
  ContentTypesResult,
  EntriesResult,
  NodesResult,
  SimpleMigrationResult,
} from '~/services';
import {
  mapCliArgsToPayload,
  mapMutedPayload,
} from '~/transformations/mapCliArgsToPayload';
import { logError, Logger } from '~/util/logger';
import { LIB_VERSION } from '~/util/version';

export const migrateCLI = async () => {
  // Welcome message, reading arguments...
  Logger.success(`
Migratortron-1000 v${LIB_VERSION} - Contensis CMS Migration Tool
  
The 1000 Series Migratortron is a fully autonomous advanced prototype. The M-1000 is made entirely out of a liquid metal called "mimetic polyalloy", meaning it can reform into any shape it touches of the approximate size. The usual modus operandi of the M-1000 is to impersonate found content, committing its attack once the target has been identified.

Parsing supplied parameters, identifying next target...
  `);

  // Build up MigrateRequest payload object from CLI args
  const argv = minimist(process.argv.slice(2));
  const isPreview = argv.commit === 'true' ? false : true;

  // Map operations so we can trigger individual service calls
  const operations: string[] = argv.operations?.toString().split(',') || [
    'everything',
  ];

  const allowedOperations = [
    'everything',
    'assets',
    'models',
    'entries',
    'nodes',
    'delete-assets',
    'delete-models',
    'delete-entries',
    'delete-nodes',
  ];

  // fire error for unrecognised operations and exit
  if (operations.some((o) => !allowedOperations.includes(o))) {
    const operationError = new Error(
      `Unrecognised operation(s): ${operations.filter(
        (o) => !allowedOperations.includes(o)
      )}`
    );
    logError(operationError, operationError.message, 'critical');
    process.exit(1);
  }
  // fire error for disallowed operation combinations and exit
  if (
    operations.some((o) => o.startsWith('delete-')) &&
    operations.some((o) => !o.startsWith('delete-'))
  ) {
    const operationError = new Error(
      `Cannot complete delete and migrate operations in the same instance`
    );
    logError(operationError, operationError.message, 'critical');
    process.exit(1);
  }

  // all arg mappings in mapper file
  const payload = mapCliArgsToPayload(argv);
  // mute sensistive payload data such as passwords
  const mutedPayload = mapMutedPayload(payload);

  // Output parsed payload to console
  Logger.success('Parsed environments as follows:');
  Logger.json(mutedPayload);
  Logger.warning(
    `Migrating: ${operations}${isPreview ? ' (preview only)' : ''}`
  );

  if (!isPreview) {
    // Y/n prompt to commit a migration
    const { commit } = await prompts({
      type: 'confirm',
      name: 'commit',
      message: 'Can you confirm you wish to commit this migration?',
      initial: true,
    });

    Logger.warning(
      `You answered ${commit
        ? '"yes". Press [CTRL+C] to terminate the script ASAP if you have run it by mistake.'
        : '"no". Better to be safe than sorry, remember you can drop the "commit" argument to analyse content to migrate and return a preview.'
      }`,
      true
    );
    if (!commit) {
      Logger.warning(
        'Don\'t invite disaster! Always avoid saving "commit=true" argument when creating your own scripts. Instead provide it separately as an additional argument.',
        true
      );
      process.exit(0);
    }
  }

  let exitCode = 0;
  const result = { errors: [] } as {
    assets?: EntriesResult;
    entries?: EntriesResult;
    errors: AppError[];
    everything?: any;
    models?: ContentTypesResult | SimpleMigrationResult;
    nodes?: NodesResult;
  };

  const migrationService = new ContensisMigrationService(payload, isPreview);
  // Work out which operations/methods we need to run.
  try {
    if (operations.includes('everything')) {
      result.everything = await migrationService.MigrateEnvironment();
    } else {
      /* Operations should always be in the following order
          -- order is reversed when deleting
        0. Projects
        1. Assets
        2. Content models
        3. Entries
        4. Nodes
      */
      if (operations.some((o) => o.startsWith('delete-'))) {
        if (operations.includes('delete-nodes')) {
          let err: AppError | null;
          [err, result.nodes] = await migrationService.DeleteNodes();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }
        if (operations.includes('delete-entries')) {
          let err: AppError | null;
          [err, result.entries] = await migrationService.DeleteEntries();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }
        if (operations.includes('delete-models')) {
          let err: AppError | null;
          [err, result.models] = await migrationService.DeleteContentTypes();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }
        if (operations.includes('delete-assets')) {
          let err: AppError | null;
          [err, result.assets] = await migrationService.DeleteAssets();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }
      } else {
        if (operations.includes('assets')) {
          let err: AppError | null;
          [err, result.assets] = await migrationService.MigrateAssets();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }

        if (operations.includes('models')) {
          let err: AppError | null;
          [err, result.models] = await migrationService.MigrateContentModels();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }

        if (operations.includes('entries')) {
          let err: AppError | null;
          [err, result.entries] = await migrationService.MigrateEntries();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }

        if (operations.includes('nodes')) {
          let err: AppError | null;
          [err, result.nodes] = await migrationService.MigrateNodes();
          if (err) {
            logError(err);
            result.errors.push(err);
          }
        }
      }
    }
  } catch (error) {
    logError(error);
    exitCode = 1;
  } finally {
    // log result to console
    Logger.success('Migration complete.');
    Logger.json(result);

    const repos = migrationService.repos;

    // save migrateResult to file
    // TODO: capture all console output and save this with the file
    const filename = path.join(
      process.cwd(),
      `./logs/${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, '')
        .replace(/-/g, '')}-[${isPreview ? 'preview' : 'commit'
      }-${operations.toString()}]-${repos.source.alias}-to-${repos.target.alias
      }.log`
    );
    const dir = path.dirname(filename);

    // check if logs folder already exists
    if (!fs.existsSync(dir))
      fs.mkdirSync(dir, {
        recursive: true,
      });

    fs.writeFile(
      filename,
      JSON.stringify(
        deepCleaner({ operations, payload: mutedPayload, result }),
        null,
        2
      ),
      (err) => {
        if (err) logError(err, 'Problem saving output to file!');
        else Logger.success(`Saved output to file '${filename}'`);

        // exit the script
        process.exit(exitCode);
      }
    );
  }
};

void migrateCLI();
